import React, { useState } from 'react';
import Box from '../Box/Box';
import { graphql, useStaticQuery } from 'gatsby';
import { useLang } from '../../hooks/useLang';
import { rem } from 'polished';
import Grid from '../Grid/Grid';
import Button from '../Button/Button';
import RichText from '../RichText/RichText';
import { AnimatePresence, motion } from 'framer-motion';
import BookingFormModal from '../BookingFormModal/BookingFormModal';
import { Body1, Head5, Subheading1 } from '../Typography/Typography';
import CollapseContainer from '../CollapseContainer/CollapseContainer';
import CloseIcon from '../../images/icons/close.svg';
import { theme } from '../../../config/theme';
import Switch, { switchClasses } from '@mui/joy/Switch';
import { getCookie, setCookie } from '../../utils/cookies';

const cookiesBannerQuery = graphql`
    query COOKIES_BANNER_QUERY {
        allPrismicCookiesBanner {
            nodes {
                lang
                data {
                    cta_1 {
                        cta_1_link {
                            ...PRISMIC_LINK_FRAGMENT
                        }
                        cta_1_text
                        cta_1_variant
                    }
                    cta_2 {
                        cta_2_link {
                            ...PRISMIC_LINK_FRAGMENT
                        }
                        cta_2_text
                        cta_2_variant
                    }
                    description {
                        richText
                    }
                    manage_cookies_modal {
                        analytics_box_description {
                            richText
                        }
                        analytics_box_title
                        cta_1_text
                        cta_1_variant
                        cta_2_text
                        cta_2_variant
                        cta_3_text
                        cta_3_variant
                        modal_description {
                            richText
                        }
                        modal_title
                        necessary_box_always_active_text
                        necessary_box_description {
                            richText
                        }
                        necessary_box_title
                    }
                }
            }
        }
    }
`;

export const CookiesBanner = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const lang = useLang();
    const {
        allPrismicCookiesBanner: { nodes },
    } = useStaticQuery(cookiesBannerQuery);

    const [closed, setClosed] = React.useState(false);

    React.useEffect(() => {
        setClosed(Boolean(localStorage.getItem('COOKIE_BAR_CLOSED')));
        acceptAll();
    }, []);

    const node = nodes.find((node) => node.lang === lang.value);

    function closeBanner() {
        setClosed(true);
        localStorage.setItem('COOKIE_BAR_CLOSED', 'true');
    }

    if (!node) return null;

    const { description, cta_1, cta_2, manage_cookies_modal } = node.data;
    const manageCookiesModal = manage_cookies_modal[0];
    const { cta_1_variant, cta_1_text } = cta_1[0];
    const { cta_2_variant, cta_2_text } = cta_2[0];

    const toggleDisableAnalytics = (checked) => {
        setCookie('analyticsConsent', checked ? 'true' : 'false', 365);
    };

    const acceptAll = () => {
        setCookie('cookieConsent', 'true', 365);
        toggleDisableAnalytics(true);
    };

    const handleAcceptAll = () => {
        acceptAll();
        closeBanner();
    };
    const handleRejectAll = () => {
        setCookie('cookieConsent', 'false', 365);
        toggleDisableAnalytics(false);
        closeBanner();
    };

    return (
        <>
            <AnimatePresence>
                {!closed && (
                    <Box
                        as={motion.div}
                        variants={{
                            initial: { opacity: 0, y: '100%' },
                            animate: {
                                opacity: 1,
                                y: 0,
                            },
                        }}
                        initial="initial"
                        animate="animate"
                        exit="initial"
                        transition={{
                            type: 'spring',
                            bounce: 0,
                            duration: 2,
                        }}
                        width="100%"
                        py={rem(48)}
                        backgroundColor="primary.50"
                        position="fixed"
                        bottom={0}
                        left={0}
                        zIndex="200"
                    >
                        <Box>
                            <Grid>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    flexDirection={{ _: 'column', lg: 'row' }}
                                    justifyContent="space-between"
                                    gridColumn={{ _: '1/-1', md: '2/-2' }}
                                    gap={rem(32)}
                                >
                                    <Box maxWidth={rem(800)}>
                                        <RichText
                                            render={description.richText}
                                        />
                                    </Box>
                                    <Box
                                        display="flex"
                                        gap={rem(16)}
                                        flexWrap={{ _: 'wrap', sm: 'nowrap' }}
                                        py={rem(8)}
                                    >
                                        <Button
                                            variant={cta_1_variant}
                                            size="lg"
                                            fullWidth={{ _: true, sm: false }}
                                            onClick={() => setIsModalOpen(true)}
                                        >
                                            {cta_1_text}
                                        </Button>
                                        <Button
                                            flex={1}
                                            variant={cta_2_variant}
                                            size="lg"
                                            onClick={handleAcceptAll}
                                            fullWidth={{ _: true, sm: false }}
                                        >
                                            {cta_2_text}
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        </Box>
                    </Box>
                )}
            </AnimatePresence>
            {!closed && isModalOpen && (
                <BookingFormModal onClose={() => setIsModalOpen(false)}>
                    <Box
                        position="relative"
                        borderRadius="sm"
                        p={rem(32)}
                        width={rem(724)}
                        backgroundColor="neutral.white"
                        mx={rem(16)}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={rem(16)}
                            mt={rem(-12)}
                        >
                            <Head5>{manageCookiesModal.modal_title}</Head5>
                            <Box
                                mr={rem(-12)}
                                onClick={() => setIsModalOpen(false)}
                                cursor="pointer"
                                p={rem(12)}
                            >
                                <Box
                                    display="block"
                                    as={CloseIcon}
                                    size={rem(24)}
                                />
                            </Box>
                        </Box>
                        <Box height={rem(440)} overflowX="auto">
                            <Box mb={rem(20)}>
                                <RichText
                                    render={
                                        manageCookiesModal.modal_description
                                            .richText
                                    }
                                />
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={rem(20)}
                            >
                                <CollapseContainer
                                    borderRadius="sm"
                                    border={`2px solid ${theme.colors.primary['100']}`}
                                    initiallyOpen
                                >
                                    <CollapseContainer.Header
                                        p={rem(16)}
                                        backgroundColor="primary.100"
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={rem(8)}
                                        >
                                            <CollapseContainer.Arrow
                                                size={24}
                                            />
                                            <Subheading1 fontWeight={600}>
                                                {
                                                    manageCookiesModal.necessary_box_title
                                                }
                                            </Subheading1>
                                        </Box>
                                        <Box ml="auto">
                                            <Body1 color="primary.main">
                                                {
                                                    manageCookiesModal.necessary_box_always_active_text
                                                }
                                            </Body1>
                                        </Box>
                                    </CollapseContainer.Header>
                                    <CollapseContainer.Item p={rem(16)}>
                                        <Body1>
                                            <RichText
                                                render={
                                                    manageCookiesModal
                                                        .necessary_box_description
                                                        .richText
                                                }
                                            />
                                        </Body1>
                                    </CollapseContainer.Item>
                                </CollapseContainer>
                                <CollapseContainer
                                    borderRadius="sm"
                                    border={`2px solid ${theme.colors.primary['100']}`}
                                >
                                    <CollapseContainer.Header
                                        p={rem(16)}
                                        backgroundColor="primary.100"
                                        display="flex"
                                        alignItems="center"
                                        userSelect="none"
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={rem(8)}
                                        >
                                            <CollapseContainer.Arrow
                                                size={24}
                                            />
                                            <Subheading1 fontWeight={600}>
                                                {
                                                    manageCookiesModal.analytics_box_title
                                                }
                                            </Subheading1>
                                        </Box>
                                        <Box ml="auto">
                                            <Switch
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                defaultChecked={
                                                    getCookie(
                                                        'analyticsConsent'
                                                    ) === 'true'
                                                }
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                    toggleDisableAnalytics(
                                                        event.target.checked
                                                    )
                                                }
                                                sx={{
                                                    '--Switch-thumbShadow':
                                                        '0 3px 7px 0 rgba(0 0 0 / 0.12)',
                                                    '--Switch-thumbSize':
                                                        '26px',
                                                    '--Switch-trackWidth':
                                                        '50px',
                                                    '--Switch-trackHeight':
                                                        '26px',
                                                    '--Switch-thumbBackground':
                                                        theme.colors
                                                            .neutral[1000],
                                                    '--Switch-trackBackground':
                                                        theme.colors
                                                            .neutral[400],
                                                    [`& .${switchClasses.thumb}`]:
                                                        {
                                                            transition:
                                                                'width 0.2s, left 0.2s',
                                                        },
                                                    '&:hover': {
                                                        '--Switch-trackBackground':
                                                            theme.colors
                                                                .neutral[400],
                                                    },
                                                    '&:active': {
                                                        '--Switch-thumbWidth':
                                                            '32px',
                                                    },
                                                    [`&.${switchClasses.checked}`]:
                                                        {
                                                            '--Switch-thumbBackground':
                                                                theme.colors
                                                                    .primary
                                                                    .main,
                                                            '--Switch-trackBackground':
                                                                theme.colors
                                                                    .primary[200],
                                                            '&:hover': {
                                                                '--Switch-trackBackground':
                                                                    theme.colors
                                                                        .primary[200],
                                                            },
                                                        },
                                                }}
                                            />
                                        </Box>
                                    </CollapseContainer.Header>
                                    <CollapseContainer.Item p={rem(16)}>
                                        <Body1>
                                            <RichText
                                                render={
                                                    manageCookiesModal
                                                        .analytics_box_description
                                                        .richText
                                                }
                                            />
                                        </Body1>
                                    </CollapseContainer.Item>
                                </CollapseContainer>
                            </Box>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            py={rem(8)}
                            mt={rem(20)}
                            gap={rem(20)}
                        >
                            <Button
                                size="lg"
                                variant={manageCookiesModal.cta_1_variant}
                                onClick={handleAcceptAll}
                                flexGrow={{ _: 1, xs: 'unset' }}
                            >
                                {manageCookiesModal.cta_1_text}
                            </Button>
                            <Button
                                size="lg"
                                variant={manageCookiesModal.cta_2_variant}
                                onClick={handleRejectAll}
                                flexGrow={{ _: 1, xs: 'unset' }}
                            >
                                {manageCookiesModal.cta_2_text}
                            </Button>
                            <Button
                                ml="auto"
                                size="lg"
                                variant={manageCookiesModal.cta_3_variant}
                                onClick={closeBanner}
                                flexGrow={{ _: 999, xs: 'unset' }}
                            >
                                {manageCookiesModal.cta_3_text}
                            </Button>
                        </Box>
                    </Box>
                </BookingFormModal>
            )}
        </>
    );
};
